/**
 * The technician's clock (FM Technician review R2-1): "data from 10:42 IST",
 * an alert's time, a consent date — in the zone of the technician's SITE,
 * always with its label, never the organisation's zone unnamed.
 *
 * Where the zone comes from, first that answers:
 *   1. GET /tech/home `sites[].timezone` (the shell reads it on every page);
 *   2. the offline pack's `sites[].timezone`, when the server sends it;
 *   3. the last zone this phone saw for this person (localStorage), so a
 *      reload with no signal still shows the site's clock;
 *   4. the organisation's zone — still labelled.
 *
 * A technician with sites in two zones gets the first site's; a row that
 * carries its own zone (an alert's `data.timezone`) uses its own.
 */
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { orgTimeZone } from '@/utils/timezone';
import { useTechHome } from '@/components/Tech/techApi';

const zoneKey = (uid: string) => `tech-zone:${uid}`;

type Sites = { timezone?: string | null }[] | null | undefined;
const firstZone = (sites: Sites): string | null => sites?.find((s) => !!s.timezone)?.timezone ?? null;

export function rememberedTechZone(uid: string | null | undefined): string | null {
  if (!uid) return null;
  try { return localStorage.getItem(zoneKey(uid)); } catch { return null; }
}

/** Without hooks (services, the offline pack): pack sites, then remembered, then the organisation. */
export function techZoneFor(uid: string | null | undefined, packSites?: Sites): string {
  return firstZone(packSites) ?? rememberedTechZone(uid) ?? orgTimeZone();
}

/** The technician's site zone; remembers it for the next offline reload. */
export function useTechZone(packSites?: Sites): string {
  const uid = useAuthStore((s) => s.user?.id ?? null);
  const home = useTechHome(!!uid);
  const fromHome = firstZone(home.data?.sites);
  useEffect(() => {
    if (!uid || !fromHome) return;
    try { localStorage.setItem(zoneKey(uid), fromHome); } catch { /* this page only */ }
  }, [uid, fromHome]);
  return fromHome ?? techZoneFor(uid, packSites);
}
